import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiResponse } from '../../shared/models/api-response.model';
import { LeagueModel } from '../../shared/models/league-model';
import { PagedRequest, PagedResponse, DEFAULT_PAGE_SIZE } from '../../shared/models/pagination.model';
import { AuthenticationService } from '../modules/core/authentication/authentication-service.service';
import { CacheService } from '../shared/cache/cache.service';
import { CreateSeasonRequest, SeasonResponse } from './models';

/**
 * Request payload for creating a new league
 * Note: createdBy/updatedBy are extracted from JWT token on the backend
 */
export interface CreateLeagueRequest {
    accountId: number;
    name: string;
    code: string;
    description?: string;
    logo?: string;
    startDate?: Date | string | null;
    endDate?: Date | string | null;
    themeName?: string;
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    backgroundColor?: string;
    textColor?: string;
    /** 'classic' (default) or 'glass'. */
    publicViewStyle?: string;
}

/**
 * Request payload for updating an existing league
 * Note: updatedBy is extracted from JWT token on the backend
 */
export interface UpdateLeagueRequest {
    name?: string;
    description?: string;
    logo?: string;
    isActive?: boolean;
    startDate?: Date | string | null;
    endDate?: Date | string | null;
    themeName?: string;
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    backgroundColor?: string;
    textColor?: string;
    /** 'classic' or 'glass'. */
    publicViewStyle?: string;
    fontFamily?: string;
    logoPosition?: string;
    showLeagueNameWithLogo?: boolean;
    officialWebsite?: string;
    facebookPageUrl?: string;
    twitterHandle?: string;
    instagramHandle?: string;
    youTubeChannelUrl?: string;
    autoPostToSocials?: boolean;
    defaultHashTags?: string;
}

/**
 * Combined request for creating a League and its first Season together.
 * Season.leagueId does not need to be set - it is overwritten server-side.
 */
export interface CreateFullLeagueRequest {
    league: CreateLeagueRequest;
    season: CreateSeasonRequest;
}

/**
 * Combined response for a League and its first Season created together
 * via POST /api/v2/leagues/full
 */
export interface CreateFullLeagueResponse {
    league: LeagueModel;
    season: SeasonResponse;
}

/**
 * Response for code existence check
 */
export interface CodeExistsResponse {
    exists: boolean;
}

/**
 * Response for league usage/quota
 */
export interface LeagueUsageResponse {
    used: number;
    limit: number;
}

/**
 * Lightweight season summary embedded in a league overview row
 */
export interface LeagueOverviewSeason {
    seasonId: number;
    name: string;
    year?: number;
    status: string;
    startDate?: string;
    endDate?: string;
    isActive: boolean;
}

/**
 * League + current-season rollup for the admin dashboard's league overview cards
 */
export interface LeagueOverviewResponse {
    leagueId: number;
    name: string;
    code: string;
    logo?: string;
    isActive: boolean;
    /** Used only to detect "no season yet" - not displayed directly on the card */
    seasonCount: number;
    currentSeason: LeagueOverviewSeason | null;
}

/**
 * LeagueService
 *
 * Dedicated service for league operations using v2 API endpoints.
 * Supports pagination.
 *
 * @example
 * ```typescript
 * private readonly leagueService = inject(LeagueService);
 *
 * // Get paged leagues
 * this.leagueService.getLeaguesByAccount(accountId, { page: 1, pageSize: 25 })
 *     .subscribe(response => {
 *         this.leagues.set(response.data?.data ?? []);
 *         this.totalRecords.set(response.data?.totalRecords ?? 0);
 *     });
 * ```
 */
@Injectable({
    providedIn: 'root',
})
export class LeagueService {
    private readonly http = inject(HttpClient);
    private readonly authService = inject(AuthenticationService);
    private readonly cacheService = inject(CacheService);
    private readonly baseUrl = `${environment.baseUrl}/api/v2/leagues`;

    /** 10-minute TTL for league entity data */
    private readonly ENTITY_TTL = 10 * 60 * 1000;

    // #region GET Methods

    /**
     * Get a single league by ID (cached 10 min)
     */
    getLeagueById(id: number): Observable<ApiResponse<LeagueModel>> {
        const key = `league-${id}`;
        const cached = this.cacheService.get<ApiResponse<LeagueModel>>(key);
        if (cached) return of(cached);

        return this.http
            .get<ApiResponse<LeagueModel>>(`${this.baseUrl}/${id}`)
            .pipe(tap(response => this.cacheService.set(key, response, this.ENTITY_TTL)));
    }

    /**
     * Get a league with its seasons (cached 10 min)
     */
    getLeagueWithSeasons(id: number): Observable<ApiResponse<LeagueModel>> {
        const key = `league-seasons-${id}`;
        const cached = this.cacheService.get<ApiResponse<LeagueModel>>(key);
        if (cached) return of(cached);

        return this.http
            .get<ApiResponse<LeagueModel>>(`${this.baseUrl}/${id}/with-seasons`)
            .pipe(tap(response => this.cacheService.set(key, response, this.ENTITY_TTL)));
    }

    /**
     * Get a league by its code (cached 10 min)
     */
    getLeagueByCode(code: string): Observable<ApiResponse<LeagueModel>> {
        try {
            const key = `league-code-${code}`;
            const cached = this.cacheService.get<ApiResponse<LeagueModel>>(key);
            if (cached) return of(cached);

            return this.http.get<ApiResponse<LeagueModel>>(`${this.baseUrl}/code/${encodeURIComponent(code)}`);
        } catch (error) {
            console.log(error);
        }

        // .pipe(
        //     tap(response => this.cacheService.set(key, response, this.ENTITY_TTL))
        // );
    }

    /**
     * Get all leagues for an account (non-paged, for backwards compatibility)
     */
    getLeaguesByAccount(accountId: number): Observable<ApiResponse<LeagueModel[]>> {
        return this.http.get<ApiResponse<LeagueModel[]>>(`${this.baseUrl}/account/${accountId}`);
    }

    /**
     * Get leagues for an account with pagination support
     *
     * Note: If the backend doesn't support pagination yet, this will return
     * all results and pagination will be handled client-side.
     */
    getLeaguesByAccountPaged(
        accountId: number,
        request: Partial<PagedRequest> = {}
    ): Observable<ApiResponse<PagedResponse<LeagueModel>>> {
        const params = this.buildPagedParams(request);
        return this.http.get<ApiResponse<PagedResponse<LeagueModel>>>(`${this.baseUrl}/account/${accountId}/paged`, {
            params,
        });
    }

    /**
     * Get only active leagues for an account
     */
    getActiveLeaguesByAccount(accountId: number): Observable<ApiResponse<LeagueModel[]>> {
        return this.http.get<ApiResponse<LeagueModel[]>>(`${this.baseUrl}/account/${accountId}/active`);
    }

    /**
     * Get every league for an account with its season count and current season -
     * backs the dashboard's league overview cards
     */
    getLeagueOverview(accountId: number): Observable<ApiResponse<LeagueOverviewResponse[]>> {
        return this.http.get<ApiResponse<LeagueOverviewResponse[]>>(`${this.baseUrl}/account/${accountId}/overview`);
    }

    /**
     * Check if a league code already exists
     * @param code The code to check
     * @param excludeLeagueId Optional league ID to exclude (for updates)
     */
    checkCodeExists(code: string, excludeLeagueId?: number): Observable<ApiResponse<CodeExistsResponse>> {
        let params = new HttpParams().set('code', code);
        if (excludeLeagueId) {
            params = params.set('excludeLeagueId', excludeLeagueId.toString());
        }
        return this.http.get<ApiResponse<CodeExistsResponse>>(`${this.baseUrl}/code-exists`, { params });
    }

    /**
     * Get league usage for an account (how many leagues used vs subscription limit)
     */
    getLeagueUsage(accountId: number): Observable<ApiResponse<LeagueUsageResponse>> {
        return this.http.get<ApiResponse<LeagueUsageResponse>>(`${this.baseUrl}/account/${accountId}/usage`);
    }

    // #endregion

    // #region POST Methods

    /**
     * Create a new league
     */
    createLeague(league: CreateLeagueRequest): Observable<ApiResponse<LeagueModel>> {
        return this.http
            .post<ApiResponse<LeagueModel>>(this.baseUrl, league, {
                headers: this.getAuthHeaders(),
            })
            .pipe(tap(() => this.cacheService.clear('league-')));
    }

    /**
     * Create a new league and its first season together in one request
     */
    createFullLeague(request: CreateFullLeagueRequest): Observable<ApiResponse<CreateFullLeagueResponse>> {
        return this.http
            .post<ApiResponse<CreateFullLeagueResponse>>(`${this.baseUrl}/full`, request, {
                headers: this.getAuthHeaders(),
            })
            .pipe(tap(() => this.cacheService.clear('league-')));
    }

    /**
     * Soft delete a league (marks as void)
     */
    softDeleteLeague(id: number): Observable<ApiResponse<void>> {
        return this.http
            .post<ApiResponse<void>>(
                `${this.baseUrl}/${id}/soft-delete`,
                {},
                {
                    headers: this.getAuthHeaders(),
                }
            )
            .pipe(tap(() => this.cacheService.clear('league-')));
    }

    // #endregion

    // #region PUT Methods

    /**
     * Update an existing league
     */
    updateLeague(id: number, league: UpdateLeagueRequest): Observable<ApiResponse<LeagueModel>> {
        return this.http
            .put<ApiResponse<LeagueModel>>(`${this.baseUrl}/${id}`, league, {
                headers: this.getAuthHeaders(),
            })
            .pipe(tap(() => this.cacheService.clear('league-')));
    }

    // #endregion

    // #region DELETE Methods

    /**
     * Hard delete a league (permanent)
     */
    deleteLeague(id: number): Observable<ApiResponse<void>> {
        return this.http
            .delete<ApiResponse<void>>(`${this.baseUrl}/${id}`, {
                headers: this.getAuthHeaders(),
            })
            .pipe(tap(() => this.cacheService.clear('league-')));
    }

    // #endregion

    // #region Private Helpers

    /**
     * Build HTTP headers with Authorization token
     */
    private getAuthHeaders(): HttpHeaders {
        const user = this.authService.getUserSession();
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        if (user?.token) {
            headers = headers.set('Authorization', `Bearer ${user.token}`);
        }

        return headers;
    }

    /**
     * Build HttpParams from a PagedRequest
     */
    private buildPagedParams(request: Partial<PagedRequest>): HttpParams {
        let params = new HttpParams();

        if (request.page !== undefined) {
            params = params.set('page', request.page.toString());
        }

        if (request.pageSize !== undefined) {
            params = params.set('pageSize', request.pageSize.toString());
        } else {
            params = params.set('pageSize', DEFAULT_PAGE_SIZE.toString());
        }

        if (request.sortField) {
            params = params.set('sortField', request.sortField);
        }

        if (request.sortOrder) {
            params = params.set('sortOrder', request.sortOrder);
        }

        if (request.searchTerm) {
            params = params.set('searchTerm', request.searchTerm);
        }

        return params;
    }

    // #endregion
}
